(() => {
  const SECTION_SELECTOR = '[data-kalaboost-before-after]';
  const CARD_SELECTOR = '[data-kalaboost-before-after-card]';
  const REVIEWS_SELECTOR = '[data-kalaboost-before-after-reviews]';
  const TRACK_SELECTOR = '[data-kalaboost-before-after-track]';
  const PREV_SELECTOR = '[data-kalaboost-before-after-prev]';
  const NEXT_SELECTOR = '[data-kalaboost-before-after-next]';
  const SCROLL_UI_SELECTOR = '[data-kalaboost-before-after-scroll-ui]';
  const PROGRESS_SELECTOR = '[data-kalaboost-before-after-progress]';
  const PROGRESS_FILL_SELECTOR = '[data-kalaboost-before-after-progress-fill]';
  const READY_ATTR = 'data-kalaboost-before-after-ready';
  const DRAG_THRESHOLD = 4;

  const getScrollContainer = (section) => section.querySelector(REVIEWS_SELECTOR);

  const getVisibleCards = (scrollContainer) =>
    Array.from(scrollContainer.querySelectorAll(CARD_SELECTOR));

  const shouldShowNav = (scrollContainer) => getVisibleCards(scrollContainer).length > 3;

  const canScroll = (scrollContainer) => {
    const visibleCards = getVisibleCards(scrollContainer);
    return scrollContainer.scrollWidth > scrollContainer.clientWidth + 1 && visibleCards.length > 0;
  };

  const getMaxScrollLeft = (scrollContainer) =>
    Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);

  const getProgressState = (scrollContainer) => {
    const maxScrollLeft = getMaxScrollLeft(scrollContainer);

    if (maxScrollLeft <= 0) {
      return { width: 100, offset: 0, value: 100 };
    }

    const thumbWidth = Math.min(
      100,
      (scrollContainer.clientWidth / scrollContainer.scrollWidth) * 100
    );
    const ratio = Math.min(1, Math.max(0, scrollContainer.scrollLeft / maxScrollLeft));
    const offset = ratio * (100 - thumbWidth);

    return {
      width: thumbWidth,
      offset,
      value: Math.min(100, Math.round(offset + thumbWidth)),
    };
  };

  const onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest(CARD_SELECTOR);
    const section = target.closest(SECTION_SELECTOR) || target.querySelector?.(SECTION_SELECTOR);
    if (!section || !card) return;

    const scrollContainer = getScrollContainer(section);
    if (!scrollContainer) return;

    card.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });

    window.requestAnimationFrame(() => {
      section._kalaboostBeforeAfterUpdateScrollUi?.();
    });
  };

  const initCarousel = (section) => {
    const scrollContainer = getScrollContainer(section);
    if (!scrollContainer || scrollContainer.dataset.kalaboostBeforeAfterCarouselReady === 'true') {
      return;
    }

    const prevButton = section.querySelector(PREV_SELECTOR);
    const nextButton = section.querySelector(NEXT_SELECTOR);
    const scrollUi = section.querySelector(SCROLL_UI_SELECTOR);
    const progressBar = section.querySelector(PROGRESS_SELECTOR);
    const progressFill = section.querySelector(PROGRESS_FILL_SELECTOR);
    const track = scrollContainer.querySelector(TRACK_SELECTOR);

    let isPointerDown = false;
    let isDragging = false;
    let preventClick = false;
    let startX = 0;
    let startScrollLeft = 0;
    let activePointerId = null;
    let scrollEndTimer = null;

    const updateScrollUi = () => {
      const showNav = shouldShowNav(scrollContainer);
      const maxScrollLeft = getMaxScrollLeft(scrollContainer);
      const atStart = scrollContainer.scrollLeft <= 2;
      const atEnd = scrollContainer.scrollLeft >= maxScrollLeft - 2;

      scrollContainer.classList.toggle('is-scrollable', showNav && canScroll(scrollContainer));

      if (prevButton) {
        prevButton.classList.toggle('is-visible', showNav);
        prevButton.disabled = !showNav || atStart;
      }

      if (nextButton) {
        nextButton.classList.toggle('is-visible', showNav);
        nextButton.disabled = !showNav || atEnd;
      }

      if (scrollUi) {
        scrollUi.classList.toggle('is-visible', showNav);
      }

      if (progressFill && showNav) {
        const progressState = getProgressState(scrollContainer);
        progressFill.style.left = `${progressState.offset}%`;
        progressFill.style.width = `${progressState.width}%`;

        if (progressBar) {
          progressBar.setAttribute('aria-valuenow', String(progressState.value));
        }
      } else if (progressFill) {
        progressFill.style.left = '0%';
        progressFill.style.width = '0%';

        if (progressBar) {
          progressBar.setAttribute('aria-valuenow', '0');
        }
      }
    };

    const releaseDocumentListeners = () => {
      document.removeEventListener('pointermove', onDocumentPointerMove);
      document.removeEventListener('pointerup', onDocumentPointerUp);
      document.removeEventListener('pointercancel', onDocumentPointerUp);
    };

    const stopDragging = () => {
      isPointerDown = false;
      isDragging = false;
      activePointerId = null;
      scrollContainer.classList.remove('is-dragging');
      releaseDocumentListeners();
    };

    const onPointerMove = (event) => {
      if (!isPointerDown || event.pointerId !== activePointerId || !shouldShowNav(scrollContainer)) return;
      if (event.pointerType === 'touch') return;

      const deltaX = event.clientX - startX;

      if (!isDragging && Math.abs(deltaX) > DRAG_THRESHOLD) {
        isDragging = true;
        preventClick = true;
        scrollContainer.classList.add('is-dragging');
      }

      if (!isDragging) return;

      event.preventDefault();
      scrollContainer.scrollLeft = startScrollLeft - deltaX;
      window.requestAnimationFrame(updateScrollUi);
    };

    const onDocumentPointerMove = (event) => {
      onPointerMove(event);
    };

    const onDocumentPointerUp = (event) => {
      if (event.pointerId !== activePointerId) return;
      stopDragging();
      updateScrollUi();
    };

    const onPointerDown = (event) => {
      if (!shouldShowNav(scrollContainer)) return;
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      isPointerDown = true;
      isDragging = false;
      activePointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = scrollContainer.scrollLeft;

      document.addEventListener('pointermove', onDocumentPointerMove);
      document.addEventListener('pointerup', onDocumentPointerUp);
      document.addEventListener('pointercancel', onDocumentPointerUp);
    };

    const onClickCapture = (event) => {
      if (!preventClick) return;
      preventClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    const onScroll = () => {
      window.requestAnimationFrame(updateScrollUi);
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(updateScrollUi, 100);
    };

    const getScrollStep = () => {
      const visibleCards = getVisibleCards(scrollContainer);
      const firstCard = visibleCards[0];
      if (!firstCard || !track) return 0;

      const trackStyles = window.getComputedStyle(track);
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;

      return firstCard.offsetWidth + gap;
    };

    const scrollNext = () => {
      if (!shouldShowNav(scrollContainer)) return;

      const step = getScrollStep();
      if (!step) return;

      scrollContainer.scrollBy({
        left: step,
        behavior: 'smooth',
      });
    };

    const scrollPrev = () => {
      if (!shouldShowNav(scrollContainer)) return;

      const step = getScrollStep();
      if (!step) return;

      scrollContainer.scrollBy({
        left: -step,
        behavior: 'smooth',
      });
    };

    const onResize = () => {
      window.requestAnimationFrame(updateScrollUi);
    };

    scrollContainer.addEventListener('pointerdown', onPointerDown);
    scrollContainer.addEventListener('click', onClickCapture, true);
    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    scrollContainer.addEventListener(
      'dragstart',
      (event) => {
        event.preventDefault();
      },
      true
    );

    prevButton?.addEventListener('click', scrollPrev);
    nextButton?.addEventListener('click', scrollNext);
    window.addEventListener('resize', onResize);

    updateScrollUi();
    scrollContainer.dataset.kalaboostBeforeAfterCarouselReady = 'true';

    section._kalaboostBeforeAfterUpdateScrollUi = updateScrollUi;
    section._kalaboostBeforeAfterCleanup = () => {
      window.clearTimeout(scrollEndTimer);
      stopDragging();
      scrollContainer.removeEventListener('pointerdown', onPointerDown);
      scrollContainer.removeEventListener('click', onClickCapture, true);
      scrollContainer.removeEventListener('scroll', onScroll);
      prevButton?.removeEventListener('click', scrollPrev);
      nextButton?.removeEventListener('click', scrollNext);
      window.removeEventListener('resize', onResize);
    };
  };

  const initSection = (section) => {
    if (!(section instanceof Element)) return;
    if (section.getAttribute(READY_ATTR) === 'true') return;

    initCarousel(section);
    section._kalaboostBeforeAfterUpdateScrollUi?.();
    section.setAttribute(READY_ATTR, 'true');
  };

  const destroySection = (section) => {
    if (!(section instanceof Element)) return;

    section._kalaboostBeforeAfterCleanup?.();

    const scrollContainer = getScrollContainer(section);
    if (scrollContainer) {
      scrollContainer.removeAttribute('data-kalaboost-before-after-carousel-ready');
    }

    delete section._kalaboostBeforeAfterUpdateScrollUi;
    delete section._kalaboostBeforeAfterCleanup;
    section.removeAttribute(READY_ATTR);
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    event.target.querySelectorAll(SECTION_SELECTOR).forEach(destroySection);
    if (event.target.matches?.(SECTION_SELECTOR)) {
      destroySection(event.target);
    }
  });

  document.addEventListener('shopify:block:select', onBlockSelect);
})();
